import React from "react";
import { Neo, NeoGrid } from "@singularsystems/neo-react";
import { observer } from "mobx-react";
import { TextConstants } from "../../../Common/TextConstants";
import InfoLabelHover from "../../../Common/Components/InfoLabelHover/InfoLabelHover";
import { Validation } from "@singularsystems/neo-core";
import RawGenericTransactionLookup from "../Models/Lookups/RawGenericTransactionLookup";
import { AuthorisableRequests } from "../../../AccessControl/Models/Commands/Security/Roles";
import UpdateCashTransactionComponentVM from "./UpdateCashTransactionComponentVM";

interface IUpdateCashTransactionComponentProps {
  viewModel: UpdateCashTransactionComponentVM;
  onBack: () => void;
  onSave: () => void;
  onAuthorisation: () => void;
  isBulkUpdate?: boolean;
  bulkSelectedCount?: number;
}

@observer
export default class UpdateCashTransactionComponent extends React.Component<IUpdateCashTransactionComponentProps> {
  public render() {
    var viewModel = this.props.viewModel;
    return (
      <>
        <Neo.Loader task={viewModel.taskRunner}>
          <Neo.Card title={TextConstants.Titles.UpdateTransactions}>
            <Neo.Form
              model={viewModel.updateCashTransactionCommand}
              validationDisplayMode={Validation.DisplayMode.AfterSubmit}
            >
              <Neo.GridLayout md={4}>
                <Neo.FormGroup
                  label={TextConstants.Labels.NewEventMapping}
                  bindContext={
                    viewModel.updateCashTransactionCommand.meta.newStaticEventID
                  }
                >
                  <Neo.AutoCompleteDropDown
                    minCharacters={3}
                    delayMs={1500}
                    bind={viewModel.updateCashTransactionCommand.meta.newStaticEventID}
                    bindDisplay={viewModel.updateCashTransactionCommand.meta.newEventName}
                    displayMember={"staticEventName"}
                    items={viewModel.filteredEvents}
                    itemSource={async (searchText) => viewModel.events.filter(event => event.staticEventName?.toLowerCase().includes(searchText.toLowerCase()))}
                  />
                </Neo.FormGroup>
              </Neo.GridLayout>
              <div className="row">
                <div className="col-lg-1">
                  <Neo.FormGroup
                    className="pt-1"
                    label={viewModel.updateCashTransactionCommand.meta.isExcludedInd.value === null
                      ? TextConstants.Labels.ExcludeValueUnchanged
                      : viewModel.updateCashTransactionCommand.meta.isExcludedInd.value
                        ? TextConstants.Labels.ExcludeAll
                        : TextConstants.Labels.IncludeAll}
                    bind={viewModel.updateCashTransactionCommand.meta.isExcludedInd}
                  />
                </div>
                <div className="col-lg-1">
                  <Neo.FormGroup
                    className="pt-1"
                    label={TextConstants.Labels.UnassignClientMapping}
                    bind={
                      viewModel.updateCashTransactionCommand.meta
                        .setClientAccountIDToNull
                    }
                    input={{ type: "switch" }}
                  />
                </div>
                <div className="col-lg-1">
                  <Neo.FormGroup
                    className="pt-1"
                    label={TextConstants.Labels.MarkAsPendingForExport}
                    bind={viewModel.updateCashTransactionCommand.meta.isSentInd}
                    input={{ type: "switch" }}
                  />
                </div>
              </div>
              <div className="row">
                <div className="pl-3">
                  <Neo.Button variant="primary"
                    onClick={() => this.props.onSave()}
                  >
                    {TextConstants.Buttons.Save}
                  </Neo.Button>
                  &nbsp;
                </div>
                {viewModel.authorisationService.hasRole(AuthorisableRequests.Edit) &&
                  <div>
                    <Neo.Button
                      variant="secondary"
                      onClick={() => this.props.onAuthorisation()}
                    >
                      {TextConstants.Buttons.RequestAuthorisation}
                    </Neo.Button>
                  </div>
                }
                &nbsp;
                <div>
                  <Neo.Button
                    onClick={() => this.props.onBack()}
                    variant="secondary"
                  >
                    {TextConstants.Buttons.Cancel}
                  </Neo.Button>
                </div>
              </div>
              {this.props.isBulkUpdate &&
                <Neo.Alert variant="warning">
                  {TextConstants.GeneralText.BulkUpdatePreview.replace("{bulkSelectedNumber}", String(this.props.bulkSelectedCount ?? 0))}
                </Neo.Alert>
              }
            </Neo.Form>
            <Neo.Card>
              <div className="inner-container">
                <NeoGrid.Grid<RawGenericTransactionLookup>
                  items={viewModel.selectedCashTransactions}
                  className={"table-striped"}
                >
                  {(data, meta) => (
                    <NeoGrid.Row>
                      <NeoGrid.Column
                        display={meta.eventDate}
                        label={TextConstants.Labels.EventDate}
                      />
                      <NeoGrid.Column
                        display={meta.clientNumber}
                        label={TextConstants.Labels.ClientNumber}
                      />
                      <NeoGrid.Column
                        display={meta.stockbrokerAccount}
                        label={TextConstants.Labels.BrokerAccount}
                      />
                      <NeoGrid.Column
                        display={meta.eventName}
                        label={TextConstants.Labels.EventName}
                      />
                      <NeoGrid.Column
                        display={meta.relatedEventID}
                        label={TextConstants.Labels.RelatedTrasactionID}
                      />
                      <NeoGrid.Column
                        display={meta.instrumentDescription}
                        label={TextConstants.Labels.InstrumentDescription}
                      />
                      <NeoGrid.Column
                        display={meta.ticker}
                        label={TextConstants.Labels.TickerSymbol}
                      />
                      <NeoGrid.Column
                        display={meta.isin}
                        label={TextConstants.Labels.Isin}
                      />
                      <NeoGrid.Column
                        display={meta.currency}
                        label={TextConstants.Labels.InstrumentCurrency}
                      />
                      <NeoGrid.Column
                        display={meta.amount}
                        label={
                          <InfoLabelHover
                            title={TextConstants.Labels.Amount}
                            content={TextConstants.Tooltips.AmountTooltip}
                          />
                        }
                        cellClassName="text-right"
                      />
                      <NeoGrid.Column
                        display={meta.amountUSD}
                        label={
                          <InfoLabelHover
                            title={TextConstants.Labels.AmountNC}
                            content={TextConstants.Tooltips.AmountUSD}
                          />
                        }
                        cellClassName="text-right"
                      />
                      <NeoGrid.Column
                        display={meta.isExcludedInd}
                        label={TextConstants.Labels.Excluded}
                      />
                    </NeoGrid.Row>
                  )}
                </NeoGrid.Grid>
              </div>
            </Neo.Card>
          </Neo.Card>
        </Neo.Loader>
      </>
    );
  }
}
